import { useMemo, useRef } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { MdOutlineCheckCircle } from "react-icons/md";
import { MdOutlineCancel } from "react-icons/md";

import DialogModal from "../DialogModal";
import { formatDate } from "../../helpers/date.util";
import { DataTable, DateColumnFilter, TableDebug } from "../DataTable";
import OverlayTooltip from "../OverlayTooltip";

import {
  useReturnsNotifications,
  usePostCustomerReturnRequest,
} from "./returnRequestHandler.util";

import { pieceTypes } from "../../constants";

import { cn } from "../../helpers/cn";
import { TooltipStandard } from "../Utility/TooltipStandard";

const pieceTypeOptions = Object.values(pieceTypes).map((type) => ({
  label: type.display,
  value: type.value,
}));

const getPieceTypeLabel = (value) => {
  const pieceType = pieceTypeOptions.find((type) => type.value === value);
  return pieceType?.label || "Pieces";
};

const formatReturnOrderRequest = (requestOrder) => ({
  ID: requestOrder?.id,
  DeliveryID: requestOrder?.deliveryId,
  Quantity: requestOrder?.quantity,
  Comment: requestOrder?.comment,
  Weight: requestOrder?.weight,
  CustomerID: requestOrder?.customerId,
  IsImported: requestOrder?.isImported,
  ClientID: requestOrder?.clientId,
  IsApproved: requestOrder?.isApproved,
  ReturnDate: requestOrder?.returnDate,
  invoiceNumber: requestOrder?.invoiceNumber,
  totalPieces: requestOrder?.totalPieces,
  customerName: requestOrder?.customerName,
  DeliveryDate: requestOrder?.deliveryDate,
  PTYPE: requestOrder?.pieceType ? requestOrder?.pieceType : " ",
  pieceType: requestOrder?.pieceType ? requestOrder?.pieceType : " ",
});

const withRenderTopToolbar = (WrappedComponent, showHistory, onShowHistory) => {
  return (instance) => {
    return (
      <WrappedComponent
        instance={instance}
        showHistory={showHistory}
        onShowHistory={onShowHistory}
      />
    );
  };
};

function ReturnRequestActionCell({ row, selectedFlatRows }) {
  const { original } = row;

  const { mutation, postSingleRequest } = usePostCustomerReturnRequest();
  const { isLoading } = mutation;

  /**
   * @type {React.MutableRefObject<{show: function():void, hide: function():void} | null>} dialogModalRef
   */
  const approvalModalRef = useRef(null);

  /**
   * @type {React.MutableRefObject<{show: function():void, hide: function():void} | null>} dialogModalRef
   */
  const rejectModalRef = useRef(null);

  // console.log("Approval Modal Ref", approvalModalRef);

  const handleOnApprove = () => {
    const requestOrder = formatReturnOrderRequest({
      ...original,
      isApproved: true,
    });

    postSingleRequest(requestOrder);

    approvalModalRef.current?.hide();
  };

  const handleOnReject = () => {
    const requestOrder = formatReturnOrderRequest({
      ...original,
      isApproved: false,
    });

    postSingleRequest(requestOrder);

    rejectModalRef.current?.hide();
  };

  if (isLoading)
    return (
      <div className="tw-flex tw-justify-center">
        <button className="btn btn-primary btn-sm" type="button" disabled>
          Loading...
        </button>
      </div>
    );

  return (
    <>
      <div className="tw-flex tw-justify-between tw-gap-x-2">
        <button
          className="btn btn-primary btn-sm"
          type="button"
          disabled={selectedFlatRows.length > 0}
          onClick={() => {
            approvalModalRef.current?.show();
          }}
        >
          Approve
        </button>

        <button
          className="btn btn-danger btn-sm"
          type="button"
          disabled={selectedFlatRows.length > 0}
          onClick={() => {
            rejectModalRef.current?.show();
          }}
        >
          Reject
        </button>
      </div>

      <DialogModal
        title="Return Order Confirmation"
        saveButtonProps={{ onClick: handleOnApprove }}
        ref={approvalModalRef}
      >
        <p>
          Are you sure you want to approve this return request?
          <br />
          Delivery ID: <strong>{original?.deliveryId}</strong>
        </p>
      </DialogModal>

      <DialogModal
        title="Return Order Rejection"
        saveButtonProps={{ onClick: handleOnReject }}
        ref={rejectModalRef}
      >
        <p>
          Are you sure you want to reject this return request?
          <br />
          Delivery ID: <strong>{original?.deliveryId}</strong>
        </p>
      </DialogModal>
    </>
  );
}

function CustomerCell({ row }) {
  const { original } = row;

  return (
    <div className="tw-flex tw-items-start tw-flex-col">
      <div className="tw-whitespace-normal">{original.customerName}</div>
      <div className="tw-whitespace-normal">{original.customerAddress}</div>
    </div>
  );
}

function ToptoolbarAction({ selectedFlatRows }) {
  const { mutation, postBatchRequests } = usePostCustomerReturnRequest();
  const { isLoading } = mutation;

  /**
   * @type {React.MutableRefObject<{show: function():void, hide: function():void} | null>} dialogModalRef
   */
  const approvalDialogModalRef = useRef(null);

  const rejectDialogModalRef = useRef(null);

  const onApproveClick = () => {
    if (selectedFlatRows.length === 0) return;
    const requestedOrders = selectedFlatRows.map((row) => {
      const { original } = row;

      const pendingReturnOrder = formatReturnOrderRequest({
        ...original,
        isApproved: true,
      });

      return pendingReturnOrder;
    });

    postBatchRequests(requestedOrders, true);
    approvalDialogModalRef.current?.hide();
  };

  const onRejectClick = () => {
    if (selectedFlatRows.length === 0) return;

    const requestedOrders = selectedFlatRows.map((row) => {
      const { original } = row;

      const pendingReturnOrder = formatReturnOrderRequest({
        ...original,
        isApproved: false,
      });

      return pendingReturnOrder;
    });

    postBatchRequests(requestedOrders, false);

    rejectDialogModalRef.current?.hide();
  };

  return (
    <div className="tw-inline-flex tw-gap-x-3 tw-mr-0 md:tw-mr-3">
      <OverlayTooltip label="Approve Selected">
        <button
          type="button"
          onClick={() => {
            approvalDialogModalRef.current?.show();
          }}
          className={cn(
            "btn tw-border-0 tw-outline-none tw-grid tw-place-content-center hover:tw-bg-gray-200 tw-px-2"
          )}
          disabled={selectedFlatRows.length === 0 || isLoading}
        >
          {/* <BsCheckLg className="tw-cursor-pointer tw-text-2xl" /> */}
          <MdOutlineCheckCircle className="tw-text-2xl" />
        </button>
      </OverlayTooltip>

      <OverlayTooltip label="Reject Selected">
        <button
          type="button"
          className={cn(
            "btn tw-border-0 tw-outline-none tw-grid tw-place-content-center hover:tw-bg-gray-200 tw-px-2"
          )}
          onClick={() => {
            rejectDialogModalRef.current?.show();
          }}
          disabled={selectedFlatRows.length === 0 || isLoading}
        >
          {/* <BsXLg className="tw-text-2xl" /> */}
          <MdOutlineCancel className="tw-text-2xl" />
        </button>
      </OverlayTooltip>

      <DialogModal
        ref={approvalDialogModalRef}
        title="Return Order Confirmation"
        saveButtonProps={{ onClick: onApproveClick }}
      >
        <p>Are you sure you want to approve the selected return requests?</p>
      </DialogModal>

      <DialogModal
        ref={rejectDialogModalRef}
        title="Return Order Rejection"
        saveButtonProps={{ onClick: onRejectClick }}
      >
        <p>Are you sure you want to reject the selected return requests?</p>
      </DialogModal>
    </div>
  );
}

function RenderWithTopToolbar({ instance, showHistory, onShowHistory }) {
  const { selectedFlatRows } = instance;

  return (
    <div className="tw-flex-grow tw-flex tw-justify-between tw-items-center">
      <OverlayTrigger
        flip
        placement="top"
        overlay={(props) => (
          <TooltipStandard className="popover-wide" {...props}>
            Show previously approved and rejected return orders
          </TooltipStandard>
        )}
      >
        <div className="tw-inline-flex tw-items-center">
          <div className="custom-control custom-switch custom-switch-md tw-pb-2">
            <input
              id="show-history-switch"
              type="checkbox"
              className="custom-control-input cursor-pointer"
              checked={showHistory}
              onChange={onShowHistory}
            />
            <label
              className="custom-control-label cursor-pointer p-0"
              htmlFor="show-history-switch"
            />
          </div>
          <span className="tw-text-lg tw-font-medium tw-text-gray-900 tw-ml-4">
            View History
          </span>
        </div>
      </OverlayTrigger>

      <div>
        <ToptoolbarAction selectedFlatRows={selectedFlatRows} />
      </div>
    </div>
  );
}

function PendingReturnRequestTable({ showHistory, onShowHistory }) {
  const { data: response, isLoading, isError } = useReturnsNotifications();

  const column = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Delivery ID",
        accessor: "deliveryId",
        maxWidth: 250,
        minWidth: 180,
        width: 180,
      },
      {
        Header: "Invoice Number",
        accessor: "invoiceNumber",
        maxWidth: 250,
        minWidth: 180,
        width: 180,
      },
      {
        Header: "Customer ID",
        accessor: "customerId",
        maxWidth: 250,
        minWidth: 180,
        width: 180,
      },
      {
        Header: "Customer",
        id: "customer",
        accessor: (originalRow) =>
          `${originalRow.customerName} ${originalRow.customerAddress}`,
        Cell: CustomerCell,
        minWidth: 200,
        width: 200,
        maxWidth: 250,
      },
      {
        Header: "Return Quantity",
        accessor: (originalRow) =>
          `${originalRow.quantity} ${getPieceTypeLabel(originalRow.pieceType)}`,
      },
      {
        Header: "Original Pieces",
        accessor: "totalPieces",
      },
      {
        Header: "Return Date",
        accessor: "returnDate",
        Cell: ({ value }) => formatDate(value, "MM/DD/YYYY hh:mm A"),
        Filter: DateColumnFilter,
        filter: "dateEqual",
        minWidth: 180,
        width: 200,
        maxWidth: 250,
      },
      {
        Header: "Delivery Date",
        accessor: "deliveryDate",
        Cell: ({ value }) => formatDate(value, "MM/DD/YYYY hh:mm A"),
        Filter: DateColumnFilter,
        filter: "dateEqual",
        minWidth: 180,
        width: 200,
        maxWidth: 250,
      },
      {
        Header: "Comment",
        accessor: "comment",
        minWidth: 200,
        width: 200,
        maxWidth: 300,
      },
      {
        Header: () => <div className="tw-text-center">Actions</div>,
        accessor: "actions",
        Cell: ReturnRequestActionCell,
        disableFilters: true,
        width: 170,
        disableResizing: true,
      },
    ],
    []
  );

  return (
    <>
      <div className="paper-panel-tight tw-rounded-2xl tw-bg-white tw-pb-5 tw-pt-4 tw-px-3">
        <DataTable
          columns={column}
          data={Array.isArray(response?.data) ? response?.data : []}
          isError={isError}
          isLoading={isLoading}
          initialState={{
            sortBy: [
              {
                id: "id",
                desc: true,
              },
            ],
            hiddenColumns: ["id"],
          }}
          renderWithTopToolbar={withRenderTopToolbar(
            RenderWithTopToolbar,
            showHistory,
            onShowHistory
          )}
          enableRowSelection
        />
      </div>

      <TableDebug
        className="tw-mt-3"
        data={Array.isArray(response?.data) ? response?.data : []}
        viewDebugMode
      />
    </>
  );
}

export default PendingReturnRequestTable;
